import { useEffect, useState } from 'react';
import type { NewsReport } from '@selfon/domain/browser';
import { request } from '../api';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { dateOnly, time } from '../format';

const when = (iso: string) => `${dateOnly(iso)} ${time(iso)}`;

/* "오늘 있었던 일" — 보호자 커뮤니티(센터 게시판 / 지역 게시판). 글은 텍스트만(사진은 아직 안전한 열람 경로가 없어 제외). */

interface BoardRef { kind: 'center' | 'region'; centerId: string; label: string; canWrite: boolean }
interface CommentView { id: string; createdAt: string; nickname: string; centerName: string | null; mine: boolean; hidden: boolean; canDelete: boolean; body: string | null; hiddenNotice: string | null }
interface PostView extends CommentView { canModerate: boolean; likeCount: number; liked: boolean; comments: CommentView[]; news?: NewsReport }
interface Objection { id: string; status: 'OPEN' | 'RESTORED' | 'DELETED'; reasonLabel: string; filedAt: string; scope: 'CENTER' | 'REGION'; isComment: boolean; text: string | null; nickname: string | null; content: string | null }

const feedApi = {
  boards: () => request<{ boards: BoardRef[]; canReview: boolean; canReviewAll: boolean; reasons: Record<string, string> }>('GET', '/me/feeds'),
  list: (b: BoardRef) => request<{ label: string; posts: PostView[] }>('GET', `/feeds/${b.kind}/${b.centerId}`),
  post: (b: BoardRef, body: string, nickname: string) => request('POST', `/feeds/${b.kind}/${b.centerId}`, { body, nickname }),
  like: (b: BoardRef, id: string, liked: boolean) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/like`, { liked }),
  comment: (b: BoardRef, id: string, body: string, nickname: string) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/comments`, { body, nickname }),
  delPost: (b: BoardRef, id: string) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/delete`),
  delComment: (b: BoardRef, id: string, cid: string) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/comments/${cid}/delete`),
  hide: (b: BoardRef, id: string, hidden: boolean) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/hide`, { hidden }),
  object: (b: BoardRef, id: string, reason: string, text: string, commentId?: string) => request('POST', `/feeds/${b.kind}/${b.centerId}/posts/${id}/objections`, { reason, text: text || undefined, commentId }),
  hqQueue: () => request<{ items: Array<Objection & { reviewCenterId: string; centerName: string }> }>('GET', '/hq/feed-objections'),
  hqDecide: (c: string, id: string, action: 'RESTORE' | 'DELETE') => request('POST', `/hq/feed-objections/${c}/${id}/decide`, { action }),
  queue: (c: string) => request<{ items: Objection[] }>('GET', `/centers/${c}/feed-objections`),
  decide: (c: string, id: string, action: 'RESTORE' | 'DELETE') => request('POST', `/centers/${c}/feed-objections/${id}/decide`, { action }),
};
const NICK_KEY = 'selfon.feed.nickname';

export function Feed() {
  const boards = useLoad(feedApi.boards, []);
  const [sel, setSel] = useState(0);
  const [tab, setTab] = useState<'board' | 'review'>('board');
  if (boards.loading && !boards.data) return <Loading />;
  if (boards.error) return <Alert>{boards.error}</Alert>;
  const list = boards.data!.boards; const b = list[sel];
  const reviewCenter = list.find((x) => x.kind === 'center' && !x.canWrite)?.centerId;
  if (boards.data!.canReviewAll) return (<><PageHeader title="오늘 있었던 일 — 이의제기 검토(본사)" sub="모든 센터·지역 게시판에서 이의제기로 감춰진 글·댓글입니다." /><Review /></>);
  return (
    <>
      <PageHeader title="오늘 있었던 일" sub="보호자끼리 일상을 나누는 게시판입니다. 센터 게시판은 우리 센터 보호자만, 지역 게시판은 같은 지역 센터 보호자가 함께 봅니다." />
      <div className="feed-tabs" role="tablist">
        {list.map((x, i) => <button key={`${x.kind}${x.centerId}`} role="tab" aria-selected={tab === 'board' && i === sel} className={tab === 'board' && i === sel ? 'btn primary' : 'btn'} onClick={() => { setSel(i); setTab('board'); }}>{x.label}</button>)}
        {boards.data!.canReview && reviewCenter && <button role="tab" aria-selected={tab === 'review'} className={tab === 'review' ? 'btn primary' : 'btn'} onClick={() => setTab('review')}>이의제기 검토</button>}
      </div>
      {tab === 'review' && reviewCenter ? <Review centerId={reviewCenter} /> : b ? <Board key={`${b.kind}${b.centerId}`} b={b} reasons={boards.data!.reasons} /> : <Empty>볼 수 있는 게시판이 없습니다. 센터에 연결된 자녀가 있으면 게시판이 열립니다.</Empty>}
    </>
  );
}

function Board({ b, reasons }: { b: BoardRef; reasons: Record<string, string> }) {
  const feed = useLoad(() => feedApi.list(b), [b.kind, b.centerId]);
  const [nick, setNick] = useState(() => localStorage.getItem(NICK_KEY) ?? '');
  const [body, setBody] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [objecting, setObjecting] = useState<{ postId: string; commentId?: string } | null>(null);
  useEffect(() => { if (nick) localStorage.setItem(NICK_KEY, nick); }, [nick]);
  const act = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); feed.reload(); } catch (e) { setErr(errorText(e)); } };
  return (
    <section>
      {b.canWrite && (
        <form className="card" onSubmit={(e) => { e.preventDefault(); void act(async () => { await feedApi.post(b, body, nick); setBody(''); }); }}>
          <Alert kind="info">아이의 이름·얼굴·학교·주소·진단명 등 개인정보는 쓰지 말아 주세요. {b.kind === 'region' ? '지역 게시판은 다른 센터 보호자도 봅니다.' : ''}</Alert>
          <Field id="feed-nick" label="닉네임" hint="2~12자. 글에는 닉네임과 센터 이름만 보입니다."><input id="feed-nick" value={nick} maxLength={12} onChange={(e) => setNick(e.target.value)} /></Field>
          <Field id="feed-body" label="오늘 있었던 일"><textarea id="feed-body" rows={3} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
          <button className="btn primary" disabled={!body.trim() || nick.trim().length < 2}>올리기</button>
        </form>
      )}
      {!b.canWrite && <Alert kind="info">보호자 게시판은 읽기만 할 수 있습니다.</Alert>}
      {err && <Alert>{err}</Alert>}
      {feed.loading && !feed.data ? <Loading /> : feed.error ? <Alert>{feed.error}</Alert> : feed.data!.posts.length === 0 ? <Empty>아직 글이 없습니다.</Empty> : feed.data!.posts.map((p) => (
        <article key={p.id} className={`card${p.news ? ' feed-news' : ''}`}>
          <header><strong>{p.nickname}</strong> <span className="muted">{p.centerName ? `· ${p.centerName} ` : ''}· {when(p.createdAt)}</span> {p.news && <Chip tone="info">자동 정리</Chip>} {p.hidden && <Chip tone="warn">감춰짐</Chip>}</header>
          {p.hiddenNotice && <p className="muted">{p.hiddenNotice}</p>}
          {p.body !== null && <p style={{ whiteSpace: 'pre-wrap' }}>{p.body}</p>}
          {p.news && <NewsBody report={p.news} />}
          <div className="row">
            {b.canWrite && !p.hidden && <button className="btn" aria-pressed={p.liked} onClick={() => void act(() => feedApi.like(b, p.id, !p.liked))}>{p.liked ? '♥' : '♡'} 좋아요 {p.likeCount}</button>}
            {!b.canWrite && <span className="muted">좋아요 {p.likeCount}</span>}
            {b.canWrite && !p.mine && !p.hidden && <button className="btn" onClick={() => setObjecting({ postId: p.id })}>이의제기</button>}
            {p.canModerate && <button className="btn" onClick={() => void act(() => feedApi.hide(b, p.id, !p.hidden))}>{p.hidden ? '다시 보이기' : '감추기'}</button>}
            {p.canDelete && <button className="btn danger" onClick={() => { if (confirm('이 글을 지울까요?')) void act(() => feedApi.delPost(b, p.id)); }}>삭제</button>}
          </div>
          <ul className="feed-comments">
            {p.comments.map((c) => (
              <li key={c.id}><strong>{c.nickname}</strong> <span className="muted">· {c.centerName}</span> {c.body ?? <span className="muted">{c.hiddenNotice}</span>}
                {c.body !== null && c.hiddenNotice && <span className="muted"> ({c.hiddenNotice})</span>}
                {b.canWrite && !c.mine && !c.hidden && <button className="btn link" onClick={() => setObjecting({ postId: p.id, commentId: c.id })}>이의제기</button>}
                {c.canDelete && <button className="btn link" onClick={() => void act(() => feedApi.delComment(b, p.id, c.id))}>삭제</button>}
              </li>
            ))}
          </ul>
          {b.canWrite && !p.hidden && <CommentBox onSend={(t) => act(() => feedApi.comment(b, p.id, t, nick))} disabled={nick.trim().length < 2} />}
        </article>
      ))}
      <ObjectionDialog target={objecting} reasons={reasons} onClose={() => setObjecting(null)} onSubmit={async (reason, text) => { await feedApi.object(b, objecting!.postId, reason, text, objecting!.commentId); feed.reload(); }} />
    </section>
  );
}

function CommentBox({ onSend, disabled }: { onSend: (t: string) => Promise<void>; disabled: boolean }) {
  const [t, setT] = useState('');
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); void onSend(t).then(() => setT('')); }}>
      <input aria-label="댓글" placeholder={disabled ? '닉네임을 먼저 적어 주세요' : '댓글 달기'} maxLength={500} value={t} onChange={(e) => setT(e.target.value)} />
      <button className="btn" disabled={disabled || !t.trim()}>댓글</button>
    </form>
  );
}

function ObjectionDialog({ target, reasons, onClose, onSubmit }: { target: { postId: string; commentId?: string } | null; reasons: Record<string, string>; onClose: () => void; onSubmit: (reason: string, text: string) => Promise<void> }) {
  const [reason, setReason] = useState('PERSONAL_INFO'); const [text, setText] = useState('');
  return (
    <Dialog title="이의제기" open={Boolean(target)} onClose={onClose} submitLabel="이의제기 보내기" onSubmit={async () => { await onSubmit(reason, text); setText(''); }}>
      <p className="muted">보내면 이 {target?.commentId ? '댓글' : '글'}은 바로 감춰지고, 작성자 센터 원장님이 확인해 복구하거나 삭제합니다. 하루 5건까지 보낼 수 있습니다.</p>
      <Field id="obj-reason" label="사유"><select id="obj-reason" value={reason} onChange={(e) => setReason(e.target.value)}>{Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
      <Field id="obj-text" label="자세한 내용(선택)"><textarea id="obj-text" rows={2} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} /></Field>
    </Dialog>
  );
}

function Review({ centerId }: { centerId?: string }) {
  const q = useLoad<{ items: Array<Objection & { reviewCenterId?: string; centerName?: string }> }>(() => (centerId ? feedApi.queue(centerId) : feedApi.hqQueue()), [centerId]);
  const [err, setErr] = useState<string | null>(null);
  if (q.loading && !q.data) return <Loading />;
  if (q.error) return <Alert>{q.error}</Alert>;
  const decide = async (id: string, a: 'RESTORE' | 'DELETE', c?: string) => { setErr(null); try { await (centerId ? feedApi.decide(centerId, id, a) : feedApi.hqDecide(c!, id, a)); q.reload(); } catch (e) { setErr(errorText(e)); } };
  return (
    <section>
      <Alert kind="info">우리 센터 보호자가 쓴 글·댓글(지역 게시판 포함)에 들어온 이의제기입니다. 접수 즉시 감춰져 있으며, 복구 또는 삭제를 정해 주세요. 처리 내역은 감사 기록에 남습니다.</Alert>
      {err && <Alert>{err}</Alert>}
      {q.data!.items.length === 0 ? <Empty>이의제기가 없습니다.</Empty> : q.data!.items.map((o) => (
        <article key={o.id} className="card">
          <header><Chip tone={o.status === 'OPEN' ? 'warn' : 'muted'}>{o.status === 'OPEN' ? '검토 대기' : o.status === 'RESTORED' ? '복구함' : '삭제함'}</Chip> {o.reasonLabel} · {o.scope === 'REGION' ? '지역' : '센터'} {o.isComment ? '댓글' : '글'}{o.centerName ? ` · ${o.centerName} 보호자 작성` : ''} · {when(o.filedAt)}</header>
          {o.text && <p>사유: {o.text}</p>}
          <blockquote>{o.nickname}: {o.content ?? '(삭제됨)'}</blockquote>
          {o.status === 'OPEN' && <div className="row"><button className="btn" onClick={() => void decide(o.id, 'RESTORE', o.reviewCenterId)}>복구</button><button className="btn danger" onClick={() => void decide(o.id, 'DELETE', o.reviewCenterId)}>삭제</button></div>}
        </article>
      ))}
    </section>
  );
}

/** 지역 교육·의료 일일 정보보고: 제목·출처·시각·한 줄 요약과 원문 링크(본문은 옮기지 않는다) */
function NewsBody({ report }: { report: NewsReport }) {
  return (
    <div className="feed-news-body">
      <h3>{report.title}</h3>
      {report.sections.map((s) => (
        <section key={s.name} aria-label={s.name}>
          <h4>{s.name} <span className="muted small">{s.items.length}건</span></h4>
          <ul>
            {s.items.map((n) => (
              <li key={n.link}>
                <a href={n.link} target="_blank" rel="noopener noreferrer nofollow">{n.title}</a>
                <span className="muted small"> · {n.source} · {time(n.publishedAt)}</span>
                {n.summary && <p className="muted small">{n.summary}</p>}
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="hint">뉴스 검색으로 매일 아침 자동으로 모은 목록입니다. 제목을 누르면 언론사·기관 원문이 열립니다. 사건·사고 기사는 넣지 않습니다.</p>
    </div>
  );
}
